import { Check } from "lucide-react";
import { usePublicConfig } from "@/lib/publicConfig";
import { sellerNet } from "@/lib/sellerNet";
import { isComplete, photoCount, MIN_PHOTOS } from "@/lib/publishQueue";
import { Mono, SearchBox, Primary, imgUrl, money } from "./ui";

const Row = ({ d, onOpen, onRemove, onRetry, game }) => {
  const f = d.form; const s = d.selected;
  const missing = photoCount(d) < MIN_PHOTOS;
  const meta = [String(f.language || "").toUpperCase(), f.condition === "SEALED" ? "SELLADO" : f.condition, `×${f.quantity || 1}`, missing && "FALTAN FOTOS"].filter(Boolean).join(" · ");
  return (
    <li className="py-2.5 border-t border-white/[.07] first:border-t-0" data-testid={`p4-item-${d.id}`}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onOpen(d)} className="w-8 h-11 rounded overflow-hidden bg-black shrink-0">{imgUrl(s.image_url) && <img src={imgUrl(s.image_url)} alt="" className="w-full h-full object-contain" />}</button>
        <button type="button" onClick={() => onOpen(d)} className="flex-1 min-w-0 text-left">
          <div className="text-[14px] font-semibold text-white truncate">{s.name}{f.version_label ? ` · ${f.version_label}` : ""}</div>
          <div className="sx-mono text-[9px] text-white/45 uppercase">{meta}</div>
        </button>
        {missing
          ? <button type="button" onClick={() => onOpen(d)} className="h-8 px-3 rounded-full border border-[#fbbf24] text-[#fbbf24] text-[12px] font-bold" data-testid={`p4-photos-${d.id}`}>+ Fotos</button>
          : <span className="text-[14px] font-bold text-white" data-testid={`p4-price-${d.id}`}>{money(f.price)}</span>}
        <button type="button" onClick={() => onRemove(d)} className="text-white/30 text-xs px-1" data-testid={`p4-remove-${d.id}`} aria-label="Quitar">✕</button>
      </div>
      {d.error && (
        <div className="mt-1 ml-11 text-[11.5px] text-[#FF6B5E]" data-testid={`p4-error-${d.id}`}>
          No se pudo publicar: {d.error}{isComplete(d, game) && <> · <button type="button" onClick={() => onRetry(d)} className="underline font-bold" data-testid={`p4-retry-${d.id}`}>Reintentar</button></>}
        </div>
      )}
    </li>
  );
};

// Lista de la cola (P4 y columna derecha de D1).
export const QueueList = ({ items, game, onOpen, onRemove, onRetry, onPublishAll, publishing, hasPayout, onPayout, compact }) => {
  const cfg = usePublicConfig();
  const ready = items.filter((d) => isComplete(d, game));
  const listed = items.reduce((a, d) => a + (parseFloat(d.form.price) || 0) * (parseInt(d.form.quantity, 10) || 1), 0);
  const net = ready.reduce((a, d) => a + sellerNet([{ price: d.form.price, quantity: d.form.quantity }], 0, "transfer", cfg).net, 0);
  const waiting = items.find((d) => photoCount(d) < MIN_PHOTOS);
  return (
    <div className="rounded-[20px] border border-white/10 bg-[#101010] p-4" data-testid="p4-queue">
      <div className="flex items-end justify-between mb-2">
        <div className="text-[22px] font-extrabold tracking-[-.04em] text-white" data-testid="p4-count">{items.length} en <span className="italic font-normal text-white/60">cola</span></div>
        {!compact && <Mono>Paso 3 de 3</Mono>}
      </div>
      {items.length === 0 ? <div className="text-[12.5px] text-white/45 py-3">Añade cartas con "+ A la cola" para publicarlas juntas.</div> : (
        <ul>{items.map((d) => <Row key={d.id} d={d} game={game} onOpen={onOpen} onRemove={onRemove} onRetry={onRetry} />)}</ul>
      )}
      {!compact && items.length > 0 && (
        <div className="flex justify-between text-[12px] mt-2 pt-2 border-t border-white/[.07]">
          <span className="text-white/55">Valor listado <b className="text-white" data-testid="p4-listed">{money(listed)}</b></span>
          <span className="text-white/55">Recibes <b className="text-[#34D399]" data-testid="p4-net">{money(net)}</b> + envío · transf.</span>
        </div>
      )}
      {hasPayout
        ? <Primary className="w-full mt-3" disabled={!ready.length} loading={publishing} loadingText="Publicando…" onClick={onPublishAll} data-testid="p4-publish-all">Publicar {ready.length} lista{ready.length === 1 ? "" : "s"}</Primary>
        : <Primary className="w-full mt-3" onClick={onPayout} data-testid="p4-add-payout">Agrega datos de cobro</Primary>}
      {!compact && waiting && <div className="text-[11px] text-white/45 text-center mt-2" data-testid="p4-note">{waiting.selected.name} espera sus fotos · con tarjeta recibes un poco menos (Payphone {cfg.processing_pct ?? 5.75}%)</div>}
    </div>
  );
};

// P4 · Cola · carga en serie
export const P4Cola = ({ justAdded, onUndo, q, setQ, ...rest }) => (
  <div className="space-y-4" data-testid="p4-cola">
    {justAdded && (
      <div className="flex items-center gap-3 p-3 rounded-[14px] border border-emerald-400/40 bg-emerald-400/5" data-testid="p4-added">
        <span className="w-6 h-6 rounded-full bg-emerald-400 grid place-items-center shrink-0"><Check size={14} className="text-black" strokeWidth={3} /></span>
        <span className="flex-1 text-[13px] text-white/80"><b className="text-white">{justAdded.selected.name}</b> en la cola · busca la siguiente</span>
        <button type="button" onClick={onUndo} className="text-[13px] font-bold text-white/80" data-testid="p4-undo">Deshacer</button>
      </div>
    )}
    <SearchBox value={q} onChange={setQ} placeholder="Siguiente carta…" autoFocus testid="p4-search" />
    <div className="text-[12px] text-white/50 -mt-2">Idioma, condición y envío se mantienen para la siguiente.</div>
    <QueueList {...rest} />
  </div>
);
